/**
 * Comparison Chart Section Component
 * Renders a single country comparison chart (stacked bar chart)
 * Countries are series (stacks), not years
 */

import React, { useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { withChartErrorBoundary } from './ChartErrorBoundary';
import { ChartTypeFactory, ChartType, ChartOrientation, DataType } from '../services/UnifiedChartFactory';
import { ComparisonChartData } from '../types/comparison.types';
import { preferencesService } from '../services/UserPreferencesService';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { comparisonChartToCSV, downloadCSV, generateExportFilename } from '../utils/exportUtils';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { getResponsiveChartHeight } from '../utils/responsiveChartUtils';

interface ComparisonChartSectionProps {
  data: ComparisonChartData;
  timeframe: string;
  weekOffset?: number;
  isMA?: boolean;
  timeGrouping?: 'weeks' | 'months' | 'quarters';
  filters?: Record<string, string[] | undefined>;
}

/**
 * Get chart configuration for comparison charts using standard createVerticalBarChart
 */
const getComparisonChartOption = (props: ChartProps): any => {
  const { data, timeframe, weekOffset = 0, timeGrouping, filters } = props;

  // Type guard to ensure data is ComparisonChartData
  if (!data || typeof data !== 'object' || !('x_axis' in data) || !('series' in data)) {
    console.warn('⚠️  Comparison chart data validation failed, returning empty chart');
    return {
      title: { text: 'No Data Available' },
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value' },
      series: []
    };
  }

  // Determine if this is M&A or funding based on title
  const isMA = data.title?.toLowerCase().includes('m&a') || data.title?.toLowerCase().includes('acquisition');

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'comparison',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  try {
    // Use standard createVerticalBarChart with COMPARISON type for automatic stacking support
    const chartOption = ChartTypeFactory.createVerticalBarChart({
      type: ChartType.COMPARISON,
      orientation: ChartOrientation.VERTICAL,
      dataType: isMA ? DataType.COUNT : DataType.CURRENCY,
      title: customTitle?.title || data.title || 'Country Comparison',
      subtitle: customTitle?.subtitle || data.subtitle || '',
      data: {
        x_axis: data.x_axis,
        series: data.series
      },
      options: {
        timeframe: (timeframe || '12w') as '1w' | '2w' | '4w' | '12w' | '6m' | 'ytd' | 'ttm' | 'custom',
        weekOffset: weekOffset,
        isMA: isMA,
        timeGrouping: timeGrouping || 'weeks'
      }
    });

    return chartOption;
  } catch (error) {
    console.error('❌ Error creating chart option:', error);
    throw error;
  }
};

/**
 * Comparison Chart Component
 */
const ComparisonChartComponent: React.FC<ChartProps & EnhancedChartProps> = (props) => {
  const { chartRef, downloadChart, isLoading, data, timeframe, filters, refreshChart } = props;

  // Title editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(() => ({
    chartType: 'comparison',
    timeframe,
    filters
  }), [timeframe, filters]);

  // Check for custom title
  const customTitle = React.useMemo(() =>
    customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get current titles from chart option
  const chartOption = React.useMemo(() =>
    getComparisonChartOption({ data, timeframe, filters } as ChartProps),
    [data, timeframe, filters, forceRefresh]
  );
  const titleOption = Array.isArray(chartOption?.title) ? chartOption.title[0] : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  // Determine if this is M&A or funding based on data title
  const isMA = data && typeof data === 'object' && 'title' in data && typeof data.title === 'string' &&
    (data.title.toLowerCase().includes('m&a') || data.title.toLowerCase().includes('acquisition'));

  // CSV Export handler
  const handleCSVExport = () => {
    if (!data || typeof data !== 'object' || !('x_axis' in data) || !('series' in data)) return;

    const chartTitle = (typeof data === 'object' && 'title' in data && typeof data.title === 'string') ? data.title : 'Comparison';
    const csvContent = comparisonChartToCSV(data, chartTitle, isMA ? 'ma' : 'funding');
    const prefix = isMA ? 'comparison-ma' : 'comparison-funding';
    const filename = generateExportFilename(prefix, 'csv');
    downloadCSV(csvContent, filename);
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: `${getResponsiveChartHeight(500, 'comparison')}px`,  // Responsive: 500px mobile, 400px tablet, 500px desktop
            minHeight: `${getResponsiveChartHeight(500, 'comparison')}px`
          }}
        />
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'comparison',
          timeframe,
          isMA: isMA
        }}
      />
    </>
  );
};

// Create wrapped component with HOC and error boundary
const ComparisonChartWithHOC = withChartErrorBoundary(
  withChartConfiguration(ComparisonChartComponent, getComparisonChartOption),
  'Comparison Chart'
);

/**
 * Main Comparison Chart Section Component
 */
export const ComparisonChartSection: React.FC<ComparisonChartSectionProps> = ({
  data,
  timeframe,
  weekOffset = 0,
  isMA = false,
  timeGrouping,
  filters
}) => {
  return (
    <div className="w-full">
      <ComparisonChartWithHOC
        data={data}
        timeframe={timeframe}
        weekOffset={weekOffset}
        timeGrouping={timeGrouping}
        filters={filters}
        chartType="bar"
        height={500}
        title={data.title}
      />
    </div>
  );
};

export default ComparisonChartSection;
